// @ vendor
const { isEmpty } = require('lodash');

// @ commons
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const { scrollToTop } = require('commonsUtilities/scrollUtils');
const { addAcceptedText } = require('../../insuranceUtilities/addAcceptedTextHelper');

// @ actions
const { requestInsuranceRelatedCurrentState } = require('./insuranceRequestRelatedState');
const { linkClient, linkClientSuccessInitialState, basicPlanAcceptanceModal } = require('./insuranceRequestRelatedWizardStep3');
const { setPaymentAccount, getPaymentAccount, requestInsuranceCurrentPricing } = require('./insuranceRequestRelatedWizardStep4');
const { getDocument, sendBudgetEmail } = require('./insuranceRequestRelatedWizardStep5');
const { modifyInsurance } = require('./insuranceRequestRelatedModify');
const { updateConsents } = require('actions/gdprConsents');
const signaturePatternActions = require('actions/signaturePattern');

// @ constants
const {
    WIZARD_REQUEST_RESPONSE_OPTIONS,
    PLANS: { BASIC }
} = require('../../insuranceConstants/insuranceRequestHome');

const {
    FIRST_STEP,
    SECOND_STEP,
    THIRD_STEP,
    FOURTH_STEP,
    FIFTH_STEP
} = require('constants/index');
const {
    INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE,
    INSURANCE_REQUEST_CHANGE_ERROR_POPULATION,
    INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR,
    INSURANCE_REQUEST_RELATED_WIZARD_LOAD_STEP,
    INSURANCE_REQUEST_RELATED_WIZARD_RESET_STEP,
    INSURANCE_REQUEST_RELATED_WIZARD_SET_VISIBLE_STEP,
    INSURANCE_REQUEST_RELATED_WIZARD_VALIDATE_STEP,
    INSURANCE_REQUEST_RELATED_WIZARD_WILL_CANCEL,
    INSURANCE_REQUEST_RELATED_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_REQUEST,
    INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_SUCCESS,
    INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_FAILURE,
    INSURANCE_REQUEST_RELATED_POST_WIZARD_SUCCESS,
    INSURANCE_REQUEST_RELATED_POST_APPRAISAL_WIZARD_SET_DATA,
    INSURANCE_REQUEST_RELATED_SET_MIN_CONTINENT_VALUE,
    INSURANCE_REQUEST_SELECT_POPULATION_REQUEST,
    INSURANCE_REQUEST_SELECT_POPULATION_FAILURE,
    INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS,
} = require('../../insuranceConstants/actionTypes');
const {
    INSURANCE_RELATED_LEGAL_CHECK,
    INSURANCE_RELATED_OTP_OPTION,
    INSURANCE_USABILITY_IMPROVEMENTS,
    PRE_CONTRACTUAL_RELATED_INSURANCE_MAIL,
} = require('constants/flagBasedFeatures');

const changeErrorPostalCode = () => ({
    type: INSURANCE_REQUEST_CHANGE_ERROR_POSTAL_CODE
});

const changeErrorPopulation = () => ({
    type: INSURANCE_REQUEST_CHANGE_ERROR_POPULATION
});

const clearPostalCodeError = () => ({
    type: INSURANCE_REQUEST_CLEAR_POSTAL_CODE_ERROR,
});

const loadStep = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_LOAD_STEP,
    data: {
        ...data,
        step
    }
});

const setVisibleStep = step => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_SET_VISIBLE_STEP,
    data: {
        step
    }
});

const validateWithUI = step => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_VALIDATE_STEP,
    data: {
        step
    }
});

const setWillCancel = willCancel => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_WILL_CANCEL,
    data: {
        willCancel
    }
});

const setData = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_SET_DATA,
    data: {
        step,
        ...data
    }
});

const setPostAppraisalInitialData = (step, data) => ({
    type: INSURANCE_REQUEST_RELATED_POST_APPRAISAL_WIZARD_SET_DATA,
    data: {
        step,
        ...data
    }
});

const cancelSimulationInProgress = () => ({
    type: INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_REQUEST
});

const cancelSimulationSuccess = () => ({
    type: INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_SUCCESS,
});

const cancelSimulationFailure = error => ({
    type: INSURANCE_REQUEST_RELATED_CANCEL_SIMULATION_FAILURE,
    data: { error }
});

const wizardFinalRequest = response => ({
    type: INSURANCE_REQUEST_RELATED_POST_WIZARD_SUCCESS,
    data: { response }
});

const setMinContinentValue = minContinentValue => ({
    type: INSURANCE_REQUEST_RELATED_SET_MIN_CONTINENT_VALUE,
    data: { minContinentValue }
});

const requestSelectPopulationInProgress = () => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_REQUEST
});

const requestSelectPopulationSuccess = population => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_SUCCESS,
    data: {
        population
    }
});

const requestSelectPopulationFailure = error => ({
    type: INSURANCE_REQUEST_SELECT_POPULATION_FAILURE,
    data: { error }
});

const resetStep = step => ({
    type: INSURANCE_REQUEST_RELATED_WIZARD_RESET_STEP,
    data: {
        step
    }
});

const getPopulationData = (postalCode, ignoreFetching) => (dispatch, getState) => {
    const immStep2 = getState().insuranceRequestWizard.getIn(['steps', SECOND_STEP]);

    if (ignoreFetching || !immStep2.get('isFetchingSelectPopulation')) {
        dispatch(requestSelectPopulationInProgress());
        const params = {
            postalCode
        };

        return APIGet({
            dispatch,
            endpoint: 'insuranceRequestPopulation',
            params
        })
            .then((response) => {
                dispatch(requestSelectPopulationSuccess(response.data));
            })
            .catch(response => {
                if (!!response.error) {
                    dispatch(requestSelectPopulationFailure(response.error));
                } else {
                    throw response;
                }
            });
    } else {
        return Promise.resolve();
    }
};

const cancelSimulation = simulationToken => dispatch => {
    dispatch(cancelSimulationInProgress());

    return APIPost({
        dispatch,
        endpoint: 'insuranceRequestCancelQuotation'
    })
        .then(() => {
            dispatch(cancelSimulationSuccess());
            if (simulationToken) {
                dispatch(linkClient(simulationToken));
            } else {
                dispatch(requestInsuranceRelatedCurrentState());
            }
        })
        .catch(response => {
            if (!!response.error) {
                dispatch(cancelSimulationFailure(response.error));
            } else {
                throw response;
            }
        });
};

const getAuthorization = (dispatch, params, immFlagBasedFeatures) => {
    dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.IS_FETCHING));
    return APIPost({
        dispatch,
        endpoint: 'insuranceRequestRelatedAuthorize',
        params
    })
        .then(() => {
            dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.SUCCESS));
            if (!__CONTACT_CENTER__ && !immFlagBasedFeatures.get(PRE_CONTRACTUAL_RELATED_INSURANCE_MAIL)) {
                dispatch(sendBudgetEmail());
            }
        })
        .catch(() => {
            dispatch(wizardFinalRequest(WIZARD_REQUEST_RESPONSE_OPTIONS.ERROR));

        })
}

const validateWizzardSuccess = () => (dispatch, getState) => {
    const prevInsuranceId = getState().insuranceRequestRelatedWizard.getIn(['steps', FIFTH_STEP, 'prevInsuranceId']);
    const insuranceRequestModify = getState().insuranceRequestModify.get('insuranceIsBeingModified');
    const immFlagBasedFeatures = getState().flagBasedFeatures;
    const previousInsurance = insuranceRequestModify ? prevInsuranceId : 0;
    const insuranceRelatedOtpOption = immFlagBasedFeatures.get(INSURANCE_RELATED_OTP_OPTION);
    const callback = (dispatch, getState, dataToken) => {
        const currentState = getState().insuranceRequestRelatedState.get('data');
        const insuranceId = currentState.get('id');
        const params = {
            query: {
                dataToken,
                insuranceId,
                previousInsurance
            }
        };
        if (insuranceRequestModify) {
            dispatch(modifyInsurance(false, true));
        }

        if (immFlagBasedFeatures.get(INSURANCE_RELATED_LEGAL_CHECK)) {
            params['query']['acceptedText'] = addAcceptedText();
        }
        if (insuranceRelatedOtpOption) {
            getAuthorization(dispatch, params, immFlagBasedFeatures);
        } else {
            dispatch(updateConsents(dataToken))
                .then(() => getAuthorization(dispatch, params, immFlagBasedFeatures))
                .catch(() => getAuthorization(dispatch, params, immFlagBasedFeatures));
        }
    };

    if (insuranceRelatedOtpOption) {
        callback(dispatch, getState, null);
    } else {
        new Promise(() => {
            signaturePatternActions.validateAndExecute(dispatch, getState, callback);
        });
    }
};

const validateStep = (step, basicPlanAccepted) => (dispatch, getState) => {
    const immFlagBasedFeatures = getState().flagBasedFeatures;

    const stepListNumber = step - 1;
    dispatch(validateWithUI(step));
    const immStep = getState().insuranceRequestRelatedWizard.getIn(['steps', stepListNumber]);
    const isValidUI = immStep.get('validUI');
    const insuranceUsabilityImprovementsFlag = !!immFlagBasedFeatures.get(INSURANCE_USABILITY_IMPROVEMENTS);
    const isBasicPlan = immStep.get('insurancePlan') === BASIC;
    const selectPlanValidation = !insuranceUsabilityImprovementsFlag ?
        isValidUI && step === FOURTH_STEP :
        isValidUI && step === FOURTH_STEP && (isBasicPlan && basicPlanAccepted || !isBasicPlan);

    if (!isValidUI) {
        scrollToTop();
    }

    if (step === FOURTH_STEP && isBasicPlan && !basicPlanAccepted) {
        dispatch(basicPlanAcceptanceModal());
    }

    if (selectPlanValidation) {
        const insurancePlan = immStep.get('insurancePlan');
        const islinkingSuccess = immStep.get('islinkingSuccess');
        if (!islinkingSuccess) {
            dispatch(linkClient(insurancePlan));
        }
        dispatch(setMinContinentValue(0));
    }
    if (isValidUI && step === 4) {
        dispatch(setPaymentAccount());
    }

    if (step === 5) {
        const isValid = immStep.get('valid');
        const isAcceptedConditions = __CONTACT_CENTER__ ? immStep.get('agreementChecked') : immStep.get('acceptedConditions');
        if (isValid && isAcceptedConditions) {
            dispatch(validateWizzardSuccess());
        }
    }
};

const loadSteps = data => dispatch => {
    if (!isEmpty(data)) {
        dispatch(loadStep(FIRST_STEP, data));
        dispatch(loadStep(SECOND_STEP, data));
        dispatch(loadStep(THIRD_STEP, data));
        dispatch(loadStep(FOURTH_STEP, data));
    }
};

module.exports = {
    cancelSimulation,
    changeErrorPostalCode,
    changeErrorPopulation,
    clearPostalCodeError,
    getAuthorization,
    getDocument,
    getPaymentAccount,
    getPopulationData,
    linkClientSuccessInitialState,
    loadSteps,
    requestInsuranceCurrentPricing,
    resetStep,
    setData,
    setPaymentAccount,
    setPostAppraisalInitialData,
    setVisibleStep,
    setWillCancel,
    validateStep,
};
